import { useState, useEffect } from "react";
import { getDashboardChallengesForUser } from "./challenges";

const typeEmoji = { count: "🏆", race: "🏁", collective: "🤝" };

export default function DashboardChallengeWidget({ userId }) {
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    getDashboardChallengesForUser(userId)
      .then(setData)
      .finally(() => setLoaded(true));
  }, [userId]);

  if (!loaded || data.length === 0) return null;

  return (
    <div style={{ marginTop: "16px", display: "grid", gap: "12px" }}>
      {data.map(({ challenge: c, leaderboard }) => {
        const ranked = leaderboard.map((p) => ({
          ...p,
          rank: leaderboard.filter((q) => (q.total || 0) > (p.total || 0)).length + 1,
        }));
        const tiedRanks = new Set(
          ranked.filter((_, __, arr) => arr.filter((q) => q.rank === _.rank).length > 1).map((p) => p.rank)
        );
        const isCollective = c.type === "collective";
        const collectiveTotal = leaderboard.reduce((s, p) => s + (p.total || 0), 0);
        const collectivePct = c.target ? Math.min(100, Math.round((collectiveTotal / c.target) * 100)) : 0;
        const myEntry = ranked.find((p) => p.userId === userId);

        return (
          <div key={c.id} style={{ background: "#f8fbff", borderRadius: "16px", border: "2px solid #bfdbfe", padding: "14px 16px" }}>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
              <span style={{ fontSize: "16px" }}>{typeEmoji[c.type] || "🏆"}</span>
              <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", flex: 1 }}>{c.title}</span>
              {myEntry && !isCollective && (
                <span style={{ fontSize: "12px", color: "#2563eb", fontWeight: "bold" }}>
                  #{myEntry.rank} · {myEntry.total || 0} {c.metric?.unit || ""}
                </span>
              )}
              {c.prize && <span style={{ fontSize: "12px", color: "#6b7280" }}>🎁 {c.prize}</span>}
            </div>

            {/* Collective progress bar */}
            {isCollective && c.target > 0 && (
              <div style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#4b6b8a", marginBottom: "3px" }}>
                  <span>Group progress</span>
                  <span>{collectiveTotal} / {c.target} {c.metric?.unit || ""} ({collectivePct}%)</span>
                </div>
                <div style={{ height: "6px", borderRadius: "999px", background: "#dbeafe", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${collectivePct}%`, background: c.collectiveReached ? "#16a34a" : "#2563eb", borderRadius: "999px" }} />
                </div>
              </div>
            )}

            {/* Mini leaderboard */}
            <div style={{ display: "grid", gap: "4px" }}>
              {ranked.map((p) => {
                const isMe = p.userId === userId;
                const isTied = tiedRanks.has(p.rank) && !isCollective;
                return (
                  <div key={p.userId} style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    background: isMe ? "#dbeafe" : isTied ? "#fef9ec" : "transparent",
                    border: isTied ? "1px solid #fbbf24" : "1px solid transparent",
                    borderRadius: "8px", padding: "4px 8px",
                  }}>
                    {!isCollective && (
                      <span style={{ fontSize: "11px", width: "20px", flexShrink: 0, color: p.rank === 1 ? "#d97706" : "#6b7280", fontWeight: p.rank === 1 ? "bold" : "normal" }}>
                        #{p.rank}
                      </span>
                    )}
                    {p.userAvatar ? (
                      <img src={p.userAvatar} alt={p.userName} style={{ width: "24px", height: "24px", borderRadius: "999px", objectFit: "cover", flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: "24px", height: "24px", borderRadius: "999px", background: "#dbeafe", flexShrink: 0 }} />
                    )}
                    <span style={{ flex: 1, fontSize: "12px", fontWeight: isMe ? "bold" : "normal", color: "#1e3a5f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.userName || "User"}{isMe ? " (you)" : ""}
                      {isTied && <span style={{ marginLeft: "5px", fontSize: "10px", color: "#b45309", fontWeight: "bold" }}>TIE</span>}
                    </span>
                    <span style={{ fontSize: "13px", fontWeight: "bold", color: "#2563eb", flexShrink: 0 }}>
                      {p.total || 0}
                    </span>
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "8px", textAlign: "right" }}>
              Ends {c.endDate}
            </div>
          </div>
        );
      })}
    </div>
  );
}
